import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import monitorApi from "../../api/monitorApi";
import Header from "../../components/Header/Header";
import ShutdownAddNav from "../../components/ShutdownAddNav/ShutdownAddNav";
import {
  ResultMessageType,
  StatusLabelInputType,
  XlsxDuplicateError,
  XlsxStatusesType,
  XlsxValidationMessage,
} from "./ShutdownAddXlsxPageTypes";

export function getFileFromChangeEvent(
  e: { target?: { files?: FileList | null } } | null | undefined,
): File | undefined {
  return e?.target?.files?.[0];
}

export function isXlsxFile(file: File): boolean {
  return file.name.toLowerCase().endsWith(".xlsx");
}

export function buildXlsxErrorMessages(
  responseData:
    | {
        message?: string;
        data?: XlsxValidationMessage[];
      }
    | null
    | undefined,
): XlsxValidationMessage[] {
  if (!responseData) {
    return [];
  }

  const errors = responseData.data ?? [];
  if (responseData.message) {
    return [responseData.message, ...errors];
  }

  return errors;
}

export const XLSX_VALIDATION_FAILED_MESSAGE_PREFIX = "Записи не добавлены";

export function isXlsxValidationSummary(
  message: XlsxValidationMessage,
): message is string {
  return (
    typeof message === "string" &&
    message.startsWith(XLSX_VALIDATION_FAILED_MESSAGE_PREFIX)
  );
}

export function isXlsxSummaryMessage(
  message: XlsxValidationMessage,
  index: number,
  text_array: XlsxValidationMessage[],
): boolean {
  return index === 0 && isXlsxValidationSummary(message);
}

export function splitXlsxErrorMessages(text_array: XlsxValidationMessage[]): {
  summaryMessage: string | null;
  errorMessages: XlsxValidationMessage[];
} {
  const first = text_array[0];
  if (first !== undefined && isXlsxValidationSummary(first)) {
    return {
      summaryMessage: first,
      errorMessages: text_array.slice(1),
    };
  }

  return { summaryMessage: null, errorMessages: text_array };
}

export function getFileFromDataTransfer(
  dataTransfer: DataTransfer | null | undefined,
): File | undefined {
  const file = dataTransfer?.files?.[0];
  if (!file || !isXlsxFile(file)) {
    return undefined;
  }
  return file;
}

export function getFileDropZoneClassName(
  isDragging: boolean,
  hasFile: boolean,
): string {
  const baseClassName =
    "flex w-full max-w-xl cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 px-8 py-8 text-center shadow-sm transition active:scale-[0.99]";

  if (isDragging) {
    return `${baseClassName} border-blue-600 bg-blue-50`;
  }

  if (hasFile) {
    return `${baseClassName} border-slate-700 bg-slate-100 hover:bg-slate-200`;
  }

  return `${baseClassName} border-dashed border-slate-500 bg-slate-100 hover:border-slate-700 hover:bg-slate-200`;
}

export function isXlsxDuplicateError(
  message: XlsxValidationMessage,
): message is XlsxDuplicateError {
  return (
    typeof message === "object" &&
    message !== null &&
    message.type === "duplicate"
  );
}

export function getDuplicateErrorMessage(_error: XlsxDuplicateError): string {
  return "Duplicate";
}

export function asPlainText(message: XlsxValidationMessage): string {
  return typeof message === "string" ? message : "";
}

export function DuplicateErrorItem({ error }: { error: XlsxDuplicateError }) {
  return (
    <div className="mb-2 last:mb-0">
      <p>{getDuplicateErrorMessage(error)}</p>
      <table className="mt-2 w-full min-w-0 border-collapse border border-black/30 text-sm">
        <colgroup>
          <col style={{ width: "5%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "27%" }} />
          <col style={{ width: "48%" }} />
        </colgroup>
        <tbody>
          <tr>
            <td className="border border-black/30 px-1 py-0.5 align-top break-words">
              {error.row}
            </td>
            <td className="border border-black/30 px-1 py-0.5 align-top break-words">
              {error.shutdown_id ?? "—"}
            </td>
            <td className="border border-black/30 px-1 py-0.5 align-top break-words">
              {error.date}
            </td>
            <td className="border border-black/30 px-1 py-0.5 align-top break-words">
              {error.region}
            </td>
            <td className="border border-black/30 px-1 py-0.5 align-top break-words">
              {error.shutdown_type}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ShutdownAddXlsxPage() {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const [file, setFile] = useState<File | null>();
  const [isDragging, setIsDragging] = useState(false);
  const [formEnabled, setFormEnabled] = useState<boolean>(false);
  const [uploadBlocked, setUploadBlocked] = useState(false);
  const [msg, setMsg] = useState<ResultMessageType>({
    text: [""],
    status: XlsxStatusesType.None,
  });

  const assignFileToInput = (selectedFile: File) => {
    /* istanbul ignore next: DataTransfer is unavailable in some test runtimes */
    if (!fileInputRef.current || typeof DataTransfer === "undefined") {
      return;
    }

    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(selectedFile);
    fileInputRef.current.files = dataTransfer.files;
  };

  const handleFileSelected = (selectedFile: File | undefined) => {
    if (selectedFile) {
      setFile(selectedFile);
      setFormEnabled(true);
      setUploadBlocked(false);
      setMsg({ text: [""], status: XlsxStatusesType.None });
      assignFileToInput(selectedFile);
      return;
    }

    setFormEnabled(false);
  };

  const blockUploadUntilFileReselected = () => {
    setUploadBlocked(true);
    /* istanbul ignore else: file input ref is attached after mount */
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const selectFile = (e) => {
    handleFileSelected(getFileFromChangeEvent(e));
  };

  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragging(false);

    const droppedFile = getFileFromDataTransfer(e.dataTransfer);
    if (droppedFile) {
      handleFileSelected(droppedFile);
      return;
    }

    if (e.dataTransfer.files?.[0]) {
      setMsg({
        text: ["Можно загружать только файлы .xlsx"],
        status: XlsxStatusesType.ServerError,
      });
    }
  };

  const resetFileSelection = () => {
    setFile(null);
    setFormEnabled(false);
    setUploadBlocked(false);
    /* istanbul ignore else: file input ref is attached after mount */
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const upload = () => {
    /* istanbul ignore else: defensive - button disabled when no file */
    if (file && !uploadBlocked) {
      let form_data = new FormData();
      form_data.append("file", file);
      setMsg({ text: ["Загружается"], status: XlsxStatusesType.Loading });
      monitorApi
        .uploadShutdownXlsx(form_data)
        .then((response) => {
          const entriesAdded = response.data?.entries_added ?? 0;
          setMsg({
            text: [`Загружено! Добавлено ${entriesAdded} записей`],
            status: XlsxStatusesType.Ok,
          });
          resetFileSelection();
        })
        .catch((error) => {
          if (error.response?.status === 422) {
            if (error.response.data) {
              setMsg({
                text: buildXlsxErrorMessages(error.response.data),
                status: XlsxStatusesType.XLSXError,
              });
            }
            blockUploadUntilFileReselected();
          } else {
            setMsg({
              text: [error.message],
              status: XlsxStatusesType.ServerError,
            });
            blockUploadUntilFileReselected();
          }
        });
    }
  };

  const isLoading = msg.status === XlsxStatusesType.Loading;

  return (
    <main className="w-full">
      <Header />
      <ShutdownAddNav />

      <div className="py-10 w-full max-w-4xl mx-auto px-4 sm:px-5 md:px-8">
        <div className="mb-2 flex justify-center">
          <label
            className={getFileDropZoneClassName(isDragging, Boolean(file))}
            data-testid="file-drop-zone"
            htmlFor="file_input"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {file ? (
              <>
                <span className="text-lg font-semibold text-gray-900 sm:text-xl">
                  Выбрано:
                </span>
                <span className="max-w-full break-all text-base text-gray-800 sm:text-lg">
                  {file.name}
                </span>
              </>
            ) : (
              <>
                <span className="text-xl font-semibold text-gray-900 sm:text-2xl">
                  Загрузить excel табличку
                </span>
                <span className="text-base text-gray-700 sm:text-lg">
                  выберите файл или перетащите сюда
                </span>
              </>
            )}
          </label>
          <input
            ref={fileInputRef}
            className="sr-only"
            id="file_input"
            type="file"
            accept=".xlsx"
            aria-label="выберите файл"
            onChange={selectFile}
          />
        </div>
        <div className="mx-auto w-full max-w-xl">
          <StatusLabel text_array={msg.text} status={msg.status} />

          <button
            disabled={!formEnabled || uploadBlocked || isLoading}
            className={
              formEnabled
                ? "mt-2 w-full rounded-lg border border-gray-950 bg-slate-100 px-4 py-3 text-lg font-medium transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:border-gray-400 disabled:bg-slate-50 disabled:text-gray-500 disabled:opacity-35"
                : "invisible mt-2 w-full"
            }
            onClick={upload}
          >
            {t("shutdown_add_page.add_button_text")}
          </button>
        </div>
      </div>
    </main>
  );
}

function StatusLabel({ text_array, status }: StatusLabelInputType) {
  const handle_status = () => {
    if (status === XlsxStatusesType.Ok || status === XlsxStatusesType.Loading) {
      return (
        <div className="bg-green-200 p-2 mb-1">
          {text_array.map((text1, index) => {
            return <p key={index}>{asPlainText(text1)}</p>;
          })}
        </div>
      );
    } else if (status === XlsxStatusesType.XLSXError) {
      const { summaryMessage, errorMessages } =
        splitXlsxErrorMessages(text_array);

      return (
        <div className="mb-1 overflow-hidden">
          {summaryMessage && (
            <div
              className="p-2 font-semibold text-red-600"
              data-testid="xlsx-error-summary"
            >
              {summaryMessage}
            </div>
          )}
          {errorMessages.length > 0 && (
            <div
              className="max-h-[50vh] overflow-y-auto p-2"
              data-testid="xlsx-error-list"
            >
              {errorMessages.map((message, index) => {
                if (isXlsxDuplicateError(message)) {
                  return <DuplicateErrorItem key={index} error={message} />;
                }

                return (
                  <p key={index} className="mb-2 last:mb-0">
                    {message}
                  </p>
                );
              })}
            </div>
          )}
        </div>
      );
    } else if (status === XlsxStatusesType.ServerError) {
      return (
        <div className="bg-red-200 p-2 mb-1">
          {text_array.map((text1, index) => {
            return <p key={index}>{asPlainText(text1)}</p>;
          })}
        </div>
      );
    } else {
      return (
        <div className="invisible">
          {text_array.map((text1, index) => {
            return <p key={index}>{asPlainText(text1)}</p>;
          })}
        </div>
      );
    }
  };
  return <div>{handle_status()}</div>;
}

export default ShutdownAddXlsxPage;
